import React, { useState } from "react";
import { motion } from "framer-motion";
import { Loader2, Snowflake, Minus, Plus, Sparkles } from "lucide-react";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
} from "@/components/ui/alert-dialog";

export const LEVELS = [
  { value: 0, label: "Vacío", src: "/stock/level-0.webp", color: "#9E9E9E" },
  { value: 25, label: "Casi vacío", src: "/stock/level-25.webp", color: "#C64545" },
  { value: 50, label: "Medio", src: "/stock/level-50.webp", color: "#F0A030" },
  { value: 75, label: "Casi lleno", src: "/stock/level-75.webp", color: "#F5D93A" },
  { value: 100, label: "Lleno", src: "/stock/level-100.webp", color: "#9EF07A" },
];

export function levelImg(level) {
  const found = LEVELS.find((l) => l.value === level);
  return (found || LEVELS[LEVELS.length - 1]).src;
}

/**
 * @param {(item, level) => Promise<void>} onChangeLevel
 * @param {(item, count) => Promise<void>} onChangeReserve
 */
export default function IceCreamCard({ item, onChangeLevel, onChangeReserve, onUseReserve, disabled = false }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingReserve, setSavingReserve] = useState(false);
  const [usingReserve, setUsingReserve] = useState(false);
  const level = typeof item.stock_level === "number" ? item.stock_level : 100;
  const reserve = typeof item.reserve_count === "number" ? item.reserve_count : 0;
  const hasReserve = reserve > 0;
  const isLow = level <= 50;
  const canUseReserve = level === 0 && reserve > 0 && !!onUseReserve;

  const pick = async (newLevel) => {
    if (newLevel === level) {
      setOpen(false);
      return;
    }
    setSaving(true);
    try {
      await onChangeLevel(item, newLevel);
      setOpen(false);
    } finally {
      setSaving(false);
    }
  };

  const bumpReserve = async (delta) => {
    if (!onChangeReserve) return;
    const next = Math.max(0, Math.min(99, reserve + delta));
    if (next === reserve) return;
    setSavingReserve(true);
    try {
      await onChangeReserve(item, next);
    } finally {
      setSavingReserve(false);
    }
  };

  const useReserve = async () => {
    if (!onUseReserve || reserve < 1) return;
    setUsingReserve(true);
    try {
      await onUseReserve(item);
      setOpen(false);
    } finally {
      setUsingReserve(false);
    }
  };

  const anySaving = saving || savingReserve || usingReserve;

  return (
    <>
      <motion.button
        type="button"
        onClick={() => !disabled && setOpen(true)}
        whileTap={{ scale: 0.96 }}
        whileHover={{ scale: disabled ? 1 : 1.02 }}
        transition={{ type: "spring", stiffness: 400, damping: 22 }}
        data-testid={`ic-card-${item.id}`}
        data-level={level}
        data-reserve={reserve}
        disabled={disabled}
        className={`group relative w-full flex flex-col rounded-2xl border bg-white text-left shadow-sm focus:outline-none focus-visible:ring-4 focus-visible:ring-[#F7C8E0] transition-shadow ${
          isLow
            ? "border-[#FF7878] ring-2 ring-[#FF7878]/40"
            : hasReserve
              ? "border-[#1E7E34] ring-2 ring-[#9EF07A]/70 shadow-[0_0_0_1px_rgba(30,126,52,0.10)]"
              : "border-[#EADBCE] hover:shadow-md"
        } ${disabled ? "opacity-70 cursor-not-allowed" : "cursor-pointer"}`}
      >
        <div className="relative aspect-square w-full p-2 bg-[#FCF9F2] rounded-t-2xl">
          <img
            src={levelImg(level)}
            alt={`${item.name} — ${level}%`}
            className="h-full w-full object-contain transition-transform duration-300 group-hover:scale-105"
          />
          <div className="absolute top-2 right-2 px-2.5 py-0.5 rounded-full bg-white/95 text-[11px] font-extrabold text-[#4A3B32] shadow-sm border border-[#EADBCE]">
            {level}%
          </div>
          {/* Reserve badge — always visible, two prominent states */}
          <div
            data-testid={`ic-reserve-badge-${item.id}`}
            className={`absolute top-2 left-2 flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-extrabold shadow-md border-2 ${
              hasReserve
                ? "bg-[#1E7E34] text-white border-[#146328]"
                : "bg-white text-[#8A796F] border-[#EADBCE]"
            }`}
          >
            <Snowflake className="w-3.5 h-3.5" />
            {hasReserve ? `×${reserve}` : "Sin reserva"}
          </div>
        </div>

        <div className="px-3 py-2 border-t border-[#EADBCE]">
          <span className="block font-display font-bold leading-tight text-[#4A3B32] line-clamp-2 text-sm sm:text-base">
            {item.name}
          </span>
          {item.marked_by && (
            <span className="text-[10px] text-[#8A796F] truncate mt-0.5 block">
              por {item.marked_by.name}
            </span>
          )}
        </div>
      </motion.button>

      <AlertDialog open={open} onOpenChange={(v) => !anySaving && setOpen(v)}>
        <AlertDialogContent
          className="rounded-3xl border-[#EADBCE] bg-white max-w-md p-6"
          data-testid={`ic-dialog-${item.id}`}
        >
          <AlertDialogHeader className="text-left">
            <AlertDialogTitle className="font-display text-2xl text-[#4A3B32]">
              {item.name}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#8A796F]">
              ¿Cuánto queda en el congelador?
            </AlertDialogDescription>
          </AlertDialogHeader>

          {canUseReserve && (
            <button
              type="button"
              onClick={useReserve}
              disabled={anySaving}
              data-testid={`ic-use-reserve-${item.id}`}
              className="mt-2 group w-full flex items-center gap-3 rounded-2xl border-2 border-[#9EF07A] bg-gradient-to-r from-[#DCF7DF] via-[#E9FCE8] to-[#DCF7DF] p-3 text-left hover:brightness-105 active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed transition-all shadow-sm"
            >
              <div className="w-11 h-11 rounded-full bg-white flex items-center justify-center shrink-0 shadow-sm">
                <Sparkles className="w-5 h-5 text-[#1E7E34]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-[#1E7E34] leading-tight">Usar reserva</div>
                <div className="text-xs text-[#2F7A3F]">
                  Rellena a 100% y quita 1 de la reserva (quedarían {reserve - 1}).
                </div>
              </div>
              {usingReserve && <Loader2 className="w-5 h-5 animate-spin text-[#1E7E34] shrink-0" />}
            </button>
          )}

          <div className="mt-4 grid grid-cols-5 gap-2">
            {LEVELS.map((l) => {
              const active = level === l.value;
              return (
                <button
                  key={l.value}
                  type="button"
                  disabled={anySaving}
                  onClick={() => pick(l.value)}
                  data-testid={`ic-level-${l.value}`}
                  className={`flex flex-col items-center gap-1 rounded-xl border-2 p-2 transition-all ${
                    active
                      ? "border-[#FF7878] bg-[#FFF0F0] ring-2 ring-[#FF7878]/30 scale-105"
                      : "border-[#EADBCE] hover:border-[#FF7878]/60 hover:bg-[#FCF9F2] active:scale-95"
                  } disabled:opacity-50`}
                >
                  <img src={l.src} alt={l.label} className="w-full aspect-square object-contain" />
                  <div className="text-center leading-tight">
                    <div className="text-[10px] font-extrabold text-[#4A3B32]">{l.value}%</div>
                    <div className="text-[9px] text-[#8A796F] leading-none">{l.label}</div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Reserve counter */}
          {onChangeReserve && (
            <div className="mt-5 pt-4 border-t border-[#EADBCE]">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5 text-sm font-bold text-[#4A3B32]">
                    <Snowflake className="w-4 h-4 text-[#1E7E34]" />
                    Reserva (congelador extra)
                  </div>
                  <p className="text-xs text-[#8A796F] mt-0.5">Cantidad de botes en reserva.</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={() => bumpReserve(-1)}
                    disabled={reserve === 0 || anySaving}
                    data-testid={`ic-reserve-minus-${item.id}`}
                    className="w-9 h-9 rounded-full border-2 border-[#EADBCE] bg-white flex items-center justify-center text-[#4A3B32] hover:border-[#FF7878]/60 hover:bg-[#FCF9F2] active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                    aria-label="Restar reserva"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <div
                    className="w-12 text-center font-display font-extrabold text-2xl text-[#4A3B32] tabular-nums"
                    data-testid={`ic-reserve-count-${item.id}`}
                  >
                    {reserve}
                  </div>
                  <button
                    type="button"
                    onClick={() => bumpReserve(1)}
                    disabled={reserve >= 99 || anySaving}
                    data-testid={`ic-reserve-plus-${item.id}`}
                    className="w-9 h-9 rounded-full border-2 border-[#9EF07A] bg-[#DCF7DF] flex items-center justify-center text-[#1E7E34] hover:bg-[#C1F0C7] active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                    aria-label="Sumar reserva"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          )}

          {anySaving && !usingReserve && (
            <div className="flex items-center justify-center mt-3 text-[#8A796F] text-sm gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Guardando...
            </div>
          )}
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
